import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Fire, CaretRight } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";

const fmt = (n) => (n || 0).toLocaleString();

function useCountdown(endsAt) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    if (!endsAt) return;
    const tick = () => {
      const ms = new Date(endsAt).getTime() - Date.now();
      if (ms <= 0) { setLeft("ended"); return; }
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      setLeft(d > 0 ? `${d}d ${h}h left` : `${h}h ${m}m left`);
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [endsAt]);
  return left;
}

function EntryRow({ e, rank }) {
  const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`;
  return (
    <Link to={`/p/${e.id}`} data-testid={`dance-entry-${e.id}`} className="flex items-center gap-3 p-2.5 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/50 transition-colors">
      <span className="w-7 text-center text-cream font-black text-sm">{medal}</span>
      <div className="w-12 h-16 rounded-xl overflow-hidden bg-white/10 shrink-0">
        {e.media_type === "video" ? <video src={fileUrl(e.media_path)} className="w-full h-full object-cover" muted /> : <img src={fileUrl(e.media_path)} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-cream font-bold text-sm truncate">@{e.author?.username}</p>
        <p className="text-cream/50 text-xs truncate">{e.caption || "Entry"}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-papaya font-black text-sm flex items-center gap-1 justify-end"><Fire size={13} weight="fill" /> {fmt(e.score)}</p>
        <p className="text-cream/40 text-[10px]">score</p>
      </div>
    </Link>
  );
}

export default function Dance() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/dance"); setData(data); }
    catch (err) { console.error("dance load failed", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const weekly = data?.weekly;
  const countdown = useCountdown(weekly?.week_ends_at);

  if (loading) return <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40">Loading…</div>;

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="dance-hub">
      <header className="sticky top-0 z-30 crystal-glass px-5 pt-6 pb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} data-testid="dance-back" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-cream">
            <ArrowLeft size={18} />
          </button>
          <div>
            <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Dance Hub</p>
            <h1 className="text-cream text-2xl font-black tracking-tighter">Challenges &amp; competitions</h1>
          </div>
        </div>
      </header>

      {weekly && (
        <section className="px-4 mt-5" data-testid="dance-weekly">
          <div className="relative rounded-3xl overflow-hidden border border-jam-gold/40">
            <img src={weekly.challenge.cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
            <div className="relative p-5 pt-24">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 rounded-full bg-jam-gold text-ink text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><Trophy size={12} weight="fill" /> Weekly Competition</span>
                <span className="text-cream/80 text-[11px] font-bold">{countdown}</span>
              </div>
              <h2 className="text-cream text-3xl font-black tracking-tighter mt-2">{weekly.challenge.emoji} {weekly.challenge.title}</h2>
              <p className="text-cream/70 text-sm mt-1">{weekly.challenge.description}</p>
              <button
                onClick={() => nav(`/create?tag=${weekly.challenge.hashtag}`)}
                data-testid="dance-weekly-enter"
                className="mt-4 px-6 py-2.5 rounded-full bg-papaya text-ink text-xs font-black tracking-[0.15em] uppercase"
              >
                Enter · #{weekly.challenge.hashtag}
              </button>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-2 px-1">Leaderboard</p>
            {weekly.entries.length === 0 ? (
              <div className="text-center py-8 px-6 rounded-2xl bg-white/5">
                <p className="text-cream/50 text-sm">No entries yet this week.</p>
                <p className="text-cream/40 text-xs mt-1">Post with #{weekly.challenge.hashtag} to take the crown.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="dance-leaderboard">
                {weekly.entries.map((e, i) => <EntryRow key={e.id} e={e} rank={i + 1} />)}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="px-4 mt-7">
        <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3 px-1">All Challenges</p>
        <div className="grid grid-cols-2 gap-3" data-testid="dance-challenges">
          {(data?.challenges || []).map((c) => (
            <Link key={c.slug} to={`/dance/${c.slug}`} data-testid={`dance-challenge-${c.slug}`} className="group relative h-32 rounded-2xl overflow-hidden border border-white/10 hover:border-papaya/60 transition-colors">
              <img src={c.cover} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
              {c.is_weekly && <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-jam-gold text-ink text-[9px] font-black tracking-[0.1em] uppercase">This week</span>}
              <div className="absolute inset-x-0 bottom-0 p-3">
                <p className="text-cream text-sm font-black leading-tight">{c.emoji} {c.title}</p>
                <p className="text-cream/60 text-[11px] flex items-center gap-1 mt-0.5">{fmt(c.entries_count)} entries <CaretRight size={10} /></p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
